import {
    LoadingScreen,
    PageTitle,
    Surface,
    Tabs,
    Text,
    useAuth,
    useThemedStyle,
} from "@venuepos/react-common";
import { useCashRegisterEditFormQuery } from "graphql-sdk";
import {
    CashRegisterConfig,
    CashRegisterSimpleConfig,
    cashRegisterConfigDefault,
    cashRegisterConfigSchema,
    cashRegisterPaymentProviderConfigDefault,
    cashRegisterPaymentProviderConfigSchema,
} from "lib";
import React, { useEffect, useState } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";

import {
    EntityConfigForm,
    EntityConfigFormEntities,
} from "../../components/entity-config";
import { RootStackParamList, RootStackScreenProps } from "../../navigation";
import { AdminContainer } from "../container";
import { CashRegisterClaimForm } from "./cash-register-claim-form";
import { CashRegisterEditForm } from "./cash-register-edit-form";
import { DeviceInfo } from "./device-info";

import type { StyleFunction } from "@venuepos/react-common";
import { FiskalyClient } from "./fiskaly/client";

type ScreenProps = RootStackScreenProps<"CASH_REGISTER_EDIT">;

export function CashRegisterEditScreen({ route }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.cash_register.write");

    const styles = useThemedStyle(styleFunc);
    const [name, setName] = useState<string>("");
    const cashRegisterId = route.params.id;
    const tabIndex = route.params.tabIndex || 0;

    const [t] = useTranslation();
    const { data } = useCashRegisterEditFormQuery({
        variables: { id: cashRegisterId },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (data && data.cashRegister) {
            setName(data.cashRegister.name);
        }
    }, [data]);

    if (!data || !data.cashRegister) {
        return <LoadingScreen style="light" />;
    }

    const tabs = [
        t("backoffice.common.configuration", "Configuration"),
        t("backoffice.common.cash_register", "Cash Register"),
        t("backoffice.common.payment_provider", "Payment provider"),
    ];

    const connectedToDevice = data.cashRegister.device !== null;
    if (connectedToDevice) {
        tabs.push(t("backoffice.cash_register.device", "Device"));
    } else {
        tabs.push(t("backoffice.cash_register.claim", "Claim"));
    }

    // Entity configs
    const entityConfigs: EntityConfigFormEntities = [];
    entityConfigs.push({
        id: data.cashRegister.merchant.id,
        type: "merchant",
    });
    if (data.cashRegister.department) {
        entityConfigs.push({
            id: data.cashRegister.department.id,
            type: "department",
        });
    }
    entityConfigs.push({
        id: cashRegisterId,
        type: "cashRegister",
        referrer: "CASH_REGISTER_EDIT" as keyof RootStackParamList,
    });

    return (
        <AdminContainer testID="cashRegisterEditScreen">
            <PageTitle>{name}</PageTitle>
            <View style={styles.container}>
                <View style={styles.left}>
                    <Surface>
                        <Tabs labels={tabs} index={Number(tabIndex)}>
                            <View style={styles.tabContent}>
                                <EntityConfigForm<CashRegisterConfig>
                                    entities={entityConfigs}
                                    defaultValues={cashRegisterConfigDefault}
                                    schema={cashRegisterConfigSchema}
                                    ignoreSections={[
                                        "paymentProviderAdyen",
                                        "paymentProviderSoftpay",
                                        "paymentProviderSumup",
                                    ]}
                                    key="cash-register-general-config"
                                />
                            </View>
                            <View style={styles.tabContent}>
                                <CashRegisterEditForm
                                    nameChanged={setName}
                                    data={data}
                                />
                            </View>
                            <View style={styles.tabContent}>
                                <EntityConfigForm<CashRegisterSimpleConfig>
                                    entities={entityConfigs}
                                    defaultValues={
                                        cashRegisterPaymentProviderConfigDefault
                                    }
                                    schema={
                                        cashRegisterPaymentProviderConfigSchema
                                    }
                                    openSection="paymentProvider"
                                    key="cash-register-payment-provider-config"
                                />
                            </View>
                            {connectedToDevice ? (
                                <View style={styles.tabContent}>
                                    <DeviceInfo
                                        device={data.cashRegister.device!}
                                        cashRegister={data.cashRegister}
                                    />
                                </View>
                            ) : (
                                <View style={styles.tabContent}>
                                    <Text style={styles.claimInfo}>
                                        {t(
                                            "backoffice.cash_register_edit.claim_device_info",
                                            "This cash register has not yet been connected to a device. Before the cash register can be used, a device has to be connected. To claim a device for this cash register, click the following button."
                                        )}
                                    </Text>
                                    <CashRegisterClaimForm
                                        cashRegisterId={cashRegisterId}
                                    />
                                </View>
                            )}
                        </Tabs>
                    </Surface>
                </View>
                <View style={styles.right}>
                    <FiskalyClient cashRegisterId={cashRegisterId} />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        ...theme.styles.row,
    },

    claimInfo: {
        marginBottom: theme.spacingScale * 2,
    },

    left: {
        marginBottom: theme.spacingScale * 2,
        marginRight: theme.spacingScale * 3,
        minWidth: 400,
    },
    right: {
        minWidth: 250,
        maxWidth: 400,
    },
    tabContent: {
        width: 600,
    },
});
